import React from 'react'
import '../styles/globals.css'
import '@splidejs/splide/dist/css/splide.min.css'
import { appWithTranslation } from 'next-i18next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { CollectiblesPurchaseContextProvider } from '@/contexts/CollectiblesPurchaseContext/CollectiblesPurchaseContextProvider'
import { useGoogleAnalyticsPageViews } from '@/layout/useGoogleAnalyticsPageViews'
import { useLoadProgressBar } from '@/layout/useLoadProgressBar'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { MasqueradeBanner } from '@/organisms/MasqueradeBanner'
import { useLocale } from '@/utils/LocaleUtil'
import { isStagingEnvironment } from '@/utils/environment-utils'
import '../wdyr'

// Extend the Window interface to include _AutofillCallbackHandler
declare global {
  interface Window {
    _AutofillCallbackHandler?: ((...args: unknown[]) => void) | null | undefined
  }
}

// Ensure _AutofillCallbackHandler is set on the window object to minimize errors from Meta
if (typeof window !== 'undefined') {
  window._AutofillCallbackHandler = window._AutofillCallbackHandler || function () {}
}

interface AppPropsWithLayout extends AppProps {
  Component: NextPageWithLayout
}

export function AngelWeb({ Component, pageProps }: AppPropsWithLayout) {
  useLoadProgressBar()
  useGoogleAnalyticsPageViews()
  const { locale } = useLocale()
  const isInContextTranslation = locale === 'ach-UG'

  const getLayout = Component.getLayout || ((page: React.ReactNode) => <DefaultLayout>{page}</DefaultLayout>)

  const layout = getLayout(
    <>
      {isStagingEnvironment() && isInContextTranslation && (
        <>
          <Script
            id="crowdin-in-context"
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html: `var _jipt = [];
  _jipt.push(['project', 'angel-localization', 'preload_texts', true]);`,
            }}
          ></Script>

          <Script type="text/javascript" src="https://cdn.crowdin.com/jipt/jipt.js" />
        </>
      )}
      <Head>
        <meta name="viewport" content="width=device-width, minimum-scale=0.5, initial-scale=1, maximum-scale=5.0" />
      </Head>
      <MasqueradeBanner />
      <Component {...pageProps} />
    </>,
    pageProps,
  )

  return <CollectiblesPurchaseContextProvider>{layout}</CollectiblesPurchaseContextProvider>
}

export default appWithTranslation(AngelWeb as React.FC<AppProps>)
